"use client"
import Link from "next/link"
import Image from "next/image"
import { useState, useEffect, useLayoutEffect } from "react"
import { useRouter } from "next/navigation"
import Icon from "@/components/ui/Icon"
import { useSession, clearCache } from "@/lib/hooks/useSession"

const navLinks = [
  { label: "Listings", href: "/listings" },
  { label: "Suburbs", href: "/suburbs" },
  { label: "Developers", href: "/developers" },
  { label: "Agents", href: "/agents" },
  { label: "Diaspora", href: "/diaspora" },
  { label: "Services", href: "/services" },
  { label: "Pricing", href: "/pricing" },
  { label: "Trust Centre", href: "/trust-centre" },
  { label: "Blog", href: "/blog" },
]

export default function Navbar() {
  const router = useRouter()
  const { user, refresh } = useSession()
  const [menuOpen, setMenuOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [favCount, setFavCount] = useState(0)
  const [msgCount, setMsgCount] = useState(0)
  const [scrolled, setScrolled] = useState(false)

  // The inline script in app/layout.tsx hides the logged-out buttons before
  // hydration when the session cache says signed in. useSession's layout effect
  // (declared above, so it runs first) has applied the cache by now, and React
  // state decides from here. Both land before the first paint.
  useLayoutEffect(() => {
    const w = window as Window & { zeAuthHint?: CSSStyleSheet }
    const hint = w.zeAuthHint
    if (!hint) return
    try { document.adoptedStyleSheets = document.adoptedStyleSheets.filter(s => s !== hint) } catch {}
    delete w.zeAuthHint
  }, [])

  useEffect(() => {
    // Fav count stays local, no auth needed
    // Navbar renders inside the ROOT layout, so a throw here escapes app/error.tsx
    // and lands on global-error — a bare "Something went wrong" on every route,
    // unrecoverable by retry. Corrupt ze_favs or blocked storage must never do that.
    const syncFavs = () => {
      let favs: unknown = []
      try { favs = JSON.parse(localStorage.getItem("ze_favs") || "[]") } catch { favs = [] }
      setFavCount(Array.isArray(favs) ? favs.length : 0)
    }
    syncFavs()
    const onScroll = () => setScrolled(window.scrollY > 12)
    window.addEventListener("scroll", onScroll, { passive: true })
    // Re-sync navbar when any page fires ze:auth (login/logout events)
    window.addEventListener("ze:auth", refresh)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("ze:auth", refresh)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Poll unread message count every 60s when logged in
  useEffect(() => {
    if (!user) { setMsgCount(0); return }
    const fetchUnread = () =>
      fetch("/api/messages/unread-count")
        .then(r => r.json())
        .then(d => setMsgCount(d.count ?? 0))
        .catch(() => {})
    fetchUnread()
    const interval = setInterval(fetchUnread, 60_000)
    return () => clearInterval(interval)
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // For signed-in users, favourites live in the DB (localStorage ze_favs only holds
  // guest favourites), so fetch the real saved count for the badge.
  useEffect(() => {
    if (!user) return
    fetch("/api/favourites")
      .then(r => r.json())
      .then(d => { if (Array.isArray(d.data)) setFavCount(d.data.length) })
      .catch(() => {})
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSignOut() {
    await fetch("/api/auth/logout", { method: "POST" })
    clearCache()
    // clearCache() only empties the store; nothing re-read it, so the navbar kept
    // rendering the signed-in menu (avatar, dashboard, favourites) for the rest of
    // the tab session. This event is already listened for above and triggers a
    // refresh that resolves the user to null.
    window.dispatchEvent(new Event("ze:auth"))
    router.push("/")
  }

  const initials = user?.name
    ? user.name.split(" ").map((n: string) => n[0]).join("").substring(0, 2).toUpperCase()
    : user?.phone?.slice(-2) ?? "ZE"

  return (
    <>
      <header className={"navbar" + (scrolled ? " navbar-scrolled" : "")}>
        <div className="navbar-inner">
          <Link href="/" className="navbar-logo">
            <Image src="/icon-on-navy.svg" width={36} height={36} alt="" style={{ borderRadius: 8, flexShrink: 0, display: "block" }} />
            <span>Zim <span className="logo-gold">Estates</span></span>
          </Link>

          <nav className="navbar-links">
            {navLinks.map(item => (
              <Link key={item.href} href={item.href} className="nav-link">{item.label}</Link>
            ))}
          </nav>

          <div className="navbar-actions">
            <Link href="/favourites" className="nav-icon" style={{ position: "relative" }}>
              <span>♡</span>
              {favCount > 0 && <span className="badge">{favCount}</span>}
            </Link>

            {user ? (
              <div style={{ position: "relative" }}>
                <button onClick={() => setMenuOpen(!menuOpen)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px 6px 6px", background: "rgba(253,251,247,0.1)", border: "1px solid rgba(253,251,247,0.2)", borderRadius: 100, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
                  <div style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--gold)", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700, color: "var(--navy)", flexShrink: 0 }}>{initials}</div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--cream)" }}>{user.name ? user.name.split(" ")[0] : "Account"}</span>
                  <span style={{ color: "rgba(253,251,247,0.6)", fontSize: 10 }}>▼</span>
                </button>
                {menuOpen && (
                  <>
                    <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 99 }} />
                    <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 14, padding: 8, minWidth: 200, boxShadow: "var(--shadow-lg)", zIndex: 100 }}>
                      <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--line)", marginBottom: 4 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)" }}>{user.name ?? "My account"}</div>
                        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{user.phone ?? user.email}</div>
                        <div style={{ fontSize: 11, color: "var(--gold-deep)", fontWeight: 600, marginTop: 2, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 4 }}>
                          {user.role === "ADMIN" && user.isSuperAdmin ? (
                            <><Icon name="Crown" size={11} /> Super Admin</>
                          ) : user.role === "ADMIN" ? (
                            <><Icon name="Shield" size={11} /> Administrator</>
                          ) : user.role}
                        </div>
                      </div>
                      {[
                        { label: "My dashboard",     href: "/dashboard",    icon: "Home",     badge: 0 },
                        { label: "Messages",          href: "/messages",     icon: "Message",  badge: msgCount > 0 ? msgCount : 0 },
                        { label: "Saved properties",  href: "/favourites",   icon: "Heart",    badge: favCount > 0 ? favCount : 0 },
                        // KYC is only for sellers/agents/developers — buyers don't need it
                        ...(user.role !== "BUYER" ? [{ label: "KYC verification",  href: "/kyc",          icon: "Verified", badge: 0 }] : []),
                        // Market reports and API keys are seller-tier features (Agency 100+ /
                        // Market Leader) — the pages themselves show an upsell if not entitled,
                        // same pattern as every other paid-tier page, so it's fine to always
                        // surface the link to any non-buyer rather than trying to predict
                        // entitlement in the nav.
                        ...(user.role !== "BUYER" ? [{ label: "Market reports",     href: "/market-reports", icon: "Stats",    badge: 0 }] : []),
                        ...(user.role !== "BUYER" ? [{ label: "API keys",          href: "/account/api-keys", icon: "Verified", badge: 0 }] : []),
                        { label: "Account settings",  href: "/account",      icon: "Settings", badge: 0 },
                        // Only agents and FSBO landlords list individual properties; developers use projects
                        ...(user.role === "AGENT" || user.role === "OWNER" ? [{ label: "List a property",   href: "/list-property",icon: "+",        badge: 0 }] : []),
                        ...(user.role === "ADMIN" ? [{ label: "Admin panel", href: "/admin", icon: "Shield", badge: 0 }] : []),
                      ].map(item => (
                        <Link key={item.label} href={item.href} onClick={() => setMenuOpen(false)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", fontSize: 13, color: "var(--navy)", borderRadius: 8, textDecoration: "none" }}
                          onMouseEnter={e => e.currentTarget.style.background = "var(--cream-warm)"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <span style={{ display: "flex", alignItems: "center", color: "var(--muted)" }}>
                            {item.icon === "+" ? "+" : <Icon name={item.icon} size={14} />}
                          </span>
                          <span style={{ flex: 1 }}>{item.label}</span>
                          {item.badge > 0 && <span style={{ minWidth: 18, height: 18, borderRadius: 9, background: "var(--navy)", color: "var(--cream)", fontSize: 10, fontWeight: 700, display: "grid", placeItems: "center", padding: "0 4px" }}>{item.badge > 99 ? "99+" : item.badge}</span>}
                        </Link>
                      ))}
                      <div style={{ borderTop: "1px solid var(--line)", marginTop: 4, paddingTop: 4 }}>
                        <button onClick={handleSignOut} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", fontSize: 13, color: "var(--red-text)", borderRadius: 8, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(196,68,68,0.05)"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <span>{'>'}</span> Sign out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                {/* nav-guest: hidden before hydration for signed-in visitors (app/layout.tsx) */}
                <Link href="/auth/login" className="btn-ghost nav-guest">Log in</Link>
                <Link href="/auth/register" className="btn-ghost btn-ghost-signup nav-guest">Sign up</Link>
                <Link href="/auth/login?next=/list-property" className="btn-gold btn-gold-nav nav-guest">List Property</Link>
              </>
            )}

            <button onClick={() => setMobileOpen(!mobileOpen)} className="mobile-menu-btn" aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen} style={{ background: "none", border: "none", color: "var(--cream)", fontSize: 22, cursor: "pointer", display: "none" }}>
              {mobileOpen ? <Icon name="X" size={22} /> : <Icon name="Menu" size={22} />}
            </button>
          </div>
        </div>

        {/* MOBILE MENU */}
        {mobileOpen && (
          <div style={{ background: "var(--navy)", borderTop: "1px solid rgba(253,251,247,0.1)", padding: "16px 24px", display: "flex", flexDirection: "column", gap: 4 }}>
            {navLinks.map(item => (
              <Link key={item.href} href={item.href} onClick={() => setMobileOpen(false)} style={{ color: "rgba(253,251,247,0.85)", fontSize: 15, fontWeight: 500, padding: "12px 0", borderBottom: "1px solid rgba(253,251,247,0.08)", textDecoration: "none" }}>{item.label}</Link>
            ))}
            {user ? (
              <>
                <Link href="/dashboard" onClick={() => setMobileOpen(false)} style={{ color: "rgba(253,251,247,0.85)", fontSize: 15, fontWeight: 500, padding: "12px 0", borderBottom: "1px solid rgba(253,251,247,0.08)", textDecoration: "none" }}>My dashboard</Link>
                <Link href="/messages" onClick={() => setMobileOpen(false)} style={{ color: "rgba(253,251,247,0.85)", fontSize: 15, fontWeight: 500, padding: "12px 0", borderBottom: "1px solid rgba(253,251,247,0.08)", textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }}>
                  Messages
                  {msgCount > 0 && <span style={{ minWidth: 18, height: 18, borderRadius: 9, background: "var(--gold)", color: "var(--navy)", fontSize: 10, fontWeight: 700, display: "grid", placeItems: "center", padding: "0 4px" }}>{msgCount > 99 ? "99+" : msgCount}</span>}
                </Link>
                <Link href="/favourites" onClick={() => setMobileOpen(false)} style={{ color: "rgba(253,251,247,0.85)", fontSize: 15, fontWeight: 500, padding: "12px 0", borderBottom: "1px solid rgba(253,251,247,0.08)", textDecoration: "none" }}>Saved properties</Link>
                {user.role !== "BUYER" && (
                  <>
                    <Link href="/market-reports" onClick={() => setMobileOpen(false)} style={{ color: "rgba(253,251,247,0.85)", fontSize: 15, fontWeight: 500, padding: "12px 0", borderBottom: "1px solid rgba(253,251,247,0.08)", textDecoration: "none" }}>Market reports</Link>
                    <Link href="/account/api-keys" onClick={() => setMobileOpen(false)} style={{ color: "rgba(253,251,247,0.85)", fontSize: 15, fontWeight: 500, padding: "12px 0", borderBottom: "1px solid rgba(253,251,247,0.08)", textDecoration: "none" }}>API keys</Link>
                  </>
                )}
                {(user.role === "AGENT" || user.role === "OWNER") && (
                  <Link href="/list-property" onClick={() => setMobileOpen(false)} style={{ color: "rgba(253,251,247,0.85)", fontSize: 15, fontWeight: 500, padding: "12px 0", borderBottom: "1px solid rgba(253,251,247,0.08)", textDecoration: "none" }}>List a property</Link>
                )}
                {user.role === "ADMIN" && (
                  <Link href="/admin" onClick={() => setMobileOpen(false)} style={{ color: "rgba(253,251,247,0.85)", fontSize: 15, fontWeight: 500, padding: "12px 0", borderBottom: "1px solid rgba(253,251,247,0.08)", textDecoration: "none" }}>Admin panel</Link>
                )}
                <div style={{ marginTop: 12 }}>
                  <button onClick={() => { handleSignOut(); setMobileOpen(false) }} style={{ width: "100%", padding: "12px", background: "rgba(196,68,68,0.15)", color: "#e57373", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Sign out</button>
                </div>
              </>
            ) : (
              <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                <Link href="/auth/login" onClick={() => setMobileOpen(false)} style={{ flex: 1, padding: "12px", background: "rgba(253,251,247,0.1)", color: "var(--cream)", borderRadius: 8, fontSize: 14, fontWeight: 600, textAlign: "center", textDecoration: "none" }}>Log in</Link>
                <Link href="/auth/register" onClick={() => setMobileOpen(false)} style={{ flex: 1, padding: "12px", background: "rgba(253,251,247,0.1)", color: "var(--cream)", borderRadius: 8, fontSize: 14, fontWeight: 600, textAlign: "center", textDecoration: "none" }}>Sign up</Link>
                <Link href="/list-property" onClick={() => setMobileOpen(false)} style={{ flex: "1 1 100%", padding: "12px", background: "var(--gold)", color: "var(--navy)", borderRadius: 8, fontSize: 14, fontWeight: 700, textAlign: "center", textDecoration: "none" }}>List Property</Link>
              </div>
            )}
          </div>
        )}
      </header>

      <style>{`
        @media (max-width: 900px) {
          .navbar-links { display: none !important; }
          .mobile-menu-btn { display: grid !important; place-items: center; width: 44px; height: 44px; margin-right: -10px; border-radius: 8px; }
          .btn-ghost { display: none !important; }
          .btn-ghost-signup { display: none !important; }
          .btn-gold-nav { display: none !important; }
        }
      `}</style>
    </>
  )
}